import * as React from 'react';
import {useSelector} from 'react-redux';

import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {SubTitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {
  getSubtractedDate,
  getTodayInTimezone,
} from '@spaced-out/ui-design-system/lib/utils';

import usePrivilege from 'src/hooks/usePrivilege';
import {useByodEnabled} from 'src/hooks/product-flags';
import {selectAgencyData} from 'src/selectors/agency';
import ErrorBoundary from 'src/components/lib/error-boundary';
import {DateRangePickerDropdown} from 'src/components/lib/date-range-picker-dropdown';
import {ByodDashboardView} from 'src/components/analytics-genesis/byod/components/byod-dashboard-view/byod-dashboard-view';
import {useByodDateRange} from 'src/components/analytics-genesis/byod/hooks/useByodDateRange';
import {useLandingDashboard} from 'src/components/analytics-genesis/byod/api';

import css from './analytics-section.css';

// Homepage analytics section: read-only embed of the agency's landing BYOD
// dashboard, rendered via the embeddable ByodDashboardView (it owns load,
// hydration, and error/retry states internally).
export default function AnalyticsSection(): React.ReactElement | null {
  const isByodEnabled = useByodEnabled();
  const canView = usePrivilege('VIEW_REPORTS_AND_DASHBOARDS');
  const canCreate = usePrivilege('CREATE_REPORTS_AND_DASHBOARDS');
  const canDelete = usePrivilege('DELETE_REPORTS_AND_DASHBOARDS');
  // Same gate as canViewByod in src/reroutes/app.tsx.
  const canViewByod = isByodEnabled && (canView || canCreate || canDelete);

  if (!canViewByod) {
    return null;
  }

  // Landing fetch decides which dashboard to embed. If it errors or no landing
  // dashboard/id is set, render nothing -- no error UI on the homepage.
  return (
    <ErrorBoundary fallback={() => null}>
      <React.Suspense fallback={<AnalyticsSectionLoader />}>
        <AnalyticsSectionInner />
      </React.Suspense>
    </ErrorBoundary>
  );
}

function AnalyticsSectionInner(): React.ReactElement | null {
  // Suspense query: data is always present here; loading suspends and errors
  // throw to the null-fallback boundary above (render nothing).
  const {data} = useLandingDashboard();

  const dashboard = data?.dashboard;
  if (!dashboard?.id) {
    // No landing dashboard/id set -> leave the container blank.
    return null;
  }

  return <AnalyticsSectionContent id={dashboard.id} name={dashboard.name} />;
}

function AnalyticsSectionLoader(): React.ReactElement {
  return (
    <div className={css.statusContainer}>
      <CircularLoader size="medium" />
    </div>
  );
}

type AnalyticsSectionContentProps = {
  id: string;
  name: string;
};

function AnalyticsSectionContent({
  id,
  name,
}: AnalyticsSectionContentProps): React.ReactElement {
  const {timezone: agencyTimezone} = useSelector(selectAgencyData);

  const today = React.useMemo(
    () => getTodayInTimezone(agencyTimezone),
    [agencyTimezone],
  );
  const minDate = React.useMemo(
    () => getSubtractedDate(today, 2, 'y'),
    [today],
  );
  const [selectedDateRange, setSelectedDateRange] = useByodDateRange();

  const handleDateRangeChange = React.useCallback(
    (dateRange: DateRangeWithTimezone) => {
      setSelectedDateRange({
        startDate: dateRange.startDate ?? today,
        endDate: dateRange.endDate ?? today,
        timezone: dateRange.timezone ?? agencyTimezone,
      });
    },
    [agencyTimezone, today, setSelectedDateRange],
  );

  return (
    <section className={css.analyticsSection} data-qa-id="analytics-section">
      <div className={css.toolbar}>
        <SubTitleMedium>{name}</SubTitleMedium>
        <DateRangePickerDropdown
          enableThisYear
          selectedDateRange={selectedDateRange}
          onRangeSelect={handleDateRangeChange}
          minDate={minDate}
        />
      </div>
      <div className={css.canvas}>
        <ByodDashboardView dashboardId={id} dateRange={selectedDateRange} />
      </div>
    </section>
  );
}
